import React, { useCallback, useEffect, useState } from "react";
import {
  BaseExpenseFieldsProps,
  ExpenseEntry,
  ExpenseFormData,
  RESPONSIBLE_PARTY_CATEGORIES,
  ResponsiblePartyCategory,
  StaffCategory,
} from "./types";
import { Field, FormikErrors, useFormikContext } from "formik";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  Icon,
  IconButton,
  TextField,
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import { Alert } from "@mui/material";
import { Typography } from "@mui/material";
import { MenuItem } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import BusinessIcon from "@mui/icons-material/Business";
import { staffApi } from "../../../services/api";
import { formatInputCurrency } from "../../../utils/utils";
import {
  commonDateFieldProps,
  commonTextFieldProps,
  smallButtonProp,
} from "../../../utils/styles";
import { Visibility } from "@mui/icons-material";

const ACTION_TYPES = ["Accepted", "Grievance", "Dispute"] as const;

const ChallanExpense = ({
  index,
  isProjectExpense = false,
}: BaseExpenseFieldsProps) => {
  const { values, setFieldValue, errors, touched } =
    useFormikContext<ExpenseFormData>();
  const [staffOptions, setStaffOptions] = useState<any[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<any>(
    values.expenses[index].responsiblePartyName || null
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string>("");
  const [isManualEntry, setIsManualEntry] = useState<boolean>(false);
  const expenseErrors = errors.expenses?.[index] as
    | FormikErrors<ExpenseEntry>
    | undefined;

  const currentActionType = values.expenses[index]?.challanAction || "";
  const [addNotes, setAddNotes] = useState<boolean>(false);
  const [displayMasterData, setDisplayMasterData] = useState<boolean>(false);

  // Get current category
  const currentResponsibleParty = values.expenses[index]
    ?.responsibleParty as ResponsiblePartyCategory;

  // Search staff data based on category
  const searchStaffData = useCallback(
    async (term: string, category: StaffCategory) => {
      if (!category) return;
      if (term.length < 2 && term.length > 0) return;

      setIsLoading(true);
      setSearchError("");

      try {
        let data: any[] = [];
        switch (category) {
          case "DRIVER":
            data = await staffApi.searchDrivers(term);
            break;
          default:
            data = [];
        }
        setStaffOptions(data);
      } catch (error) {
        setSearchError(`Failed to load ${category.toLowerCase()} data`);
        console.error(`Error searching ${category}:`, error);
        setStaffOptions([]);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // Effect to search when debounced term or category changes
  useEffect(() => {
    if (currentResponsibleParty === "DRIVER") {
      searchStaffData(searchTerm, currentResponsibleParty);
    } else {
      setStaffOptions([]);
    }
  }, [searchTerm, currentResponsibleParty, searchStaffData]);

  // Handle staff selection
  const handleStaffChange = (staff: any | null, inputValue?: string) => {
    setSelectedStaff(staff);

    if (staff) {
      // Auto-fill fields from master data
      setIsManualEntry(false);
      setFieldValue(`expenses.${index}.responsiblePartyName`, staff.name);
      setFieldValue(
        `expenses.${index}.responsiblePartyContact`,
        staff.contactNumber
      );
      setFieldValue(`expenses.${index}.responsiblePartyId`, staff.id);
      setFieldValue(`expenses.${index}.isEdited`, true);
    } else if (inputValue && inputValue.trim() !== "") {
      // Manual entry mode
      setIsManualEntry(true);
      setFieldValue(
        `expenses.${index}.responsiblePartyName`,
        inputValue.trim()
      );
      setFieldValue(`expenses.${index}.responsiblePartyId`, null);
      setFieldValue(`expenses.${index}.isEdited`, true);
    } else {
      // Clear fields
      setIsManualEntry(false);
      setFieldValue(`expenses.${index}.responsibleParty`, "");
      setFieldValue(`expenses.${index}.responsiblePartyContact`, "");
      setFieldValue(`expenses.${index}.responsiblePartyId`, null);
      setFieldValue(`expenses.${index}.isEdited`, true);
    }
  };

  // Get option label
  const getOptionLabel = (option: any) => {
    if (typeof option === "string") return option;
    return `${option.name} - ${option.contactNumber}`;
  };

  // Render staff option
  const renderStaffOption = (props: any, option: any) => (
    <Box component="li" {...props} sx={{ fontSize: "0.75rem", py: 0.5 }}>
      <Box sx={{ width: "100%" }}>
        <Typography
          variant="subtitle2"
          sx={{ fontSize: "0.75rem", fontWeight: 500 }}
        >
          {option.name}
        </Typography>
        {option.contactNumber && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.75rem", mt: 0.25 }}
          >
            Contact: {option.contactNumber}
          </Typography>
        )}
        {currentResponsibleParty === "DRIVER" && option.drivingLicense && (
          <Chip
            label={`License: ${option.drivingLicense}`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ mt: 0.5 }}
          />
        )}
      </Box>
    </Box>
  );

  const handleMarkAsPaid = useCallback(
    (isPaid: boolean) => {
      if (isPaid) {
        setFieldValue(`expenses.${index}.payments`, [
          ...values.expenses[index].payments!,
          {
            type: "ADVANCE",
            mode: "CASH",
            date: values.expenses[index].expenseDate || "",
            location: values.expenses[index].location || "",
            receivedFrom: values.expenses[index].paidTo || "",
            remarks: "",
            status: "PAID",
            isEdited: false,
            isDeleted: false,
            isNew: true,
            amount: values.expenses[index].amount,
            masterExpenseId: values.expenses[index].id || null,
          },
        ]);
      } else {
        setFieldValue(`expenses.${index}.payments`, []);
      }
    },
    [index, setFieldValue, values.expenses]
  );

  return (
    <Grid container item xs={12} spacing={1}>
      <Grid item xs={12} sm={3}>
        <Field
          as={TextField}
          label="Challan Date"
          type="date"
          name={`expenses.${index}.expenseDate`}
          fullWidth
          required
          {...commonDateFieldProps}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue(`expenses.${index}.isEdited`, true);
            setFieldValue(`expenses.${index}.expenseDate`, e.target.value);
          }}
        />
      </Grid>

      <Grid item xs={12} sm={3}>
        <Field
          as={TextField}
          label="Challan Type"
          name={`expenses.${index}.challanType`}
          fullWidth
          required
          error={Boolean(
            expenseErrors?.challanType && touched.expenses?.[index]?.challanType
          )}
          helperText={
            expenseErrors?.challanType && touched.expenses?.[index]?.challanType
              ? expenseErrors?.challanType
              : null
          }
          placeholder="Enter challan type"
          {...commonTextFieldProps}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue(`expenses.${index}.isEdited`, true);
            setFieldValue(`expenses.${index}.challanType`, e.target.value);
          }}
        />
      </Grid>

      <Grid item xs={12} sm={3}>
        <Field
          as={TextField}
          label="Challan Paid To"
          name={`expenses.${index}.paidTo`}
          fullWidth
          required
          error={Boolean(
            expenseErrors?.paidTo && touched.expenses?.[index]?.paidTo
          )}
          helperText={
            expenseErrors?.paidTo && touched.expenses?.[index]?.paidTo
              ? expenseErrors?.paidTo
              : null
          }
          placeholder="Enter challan paid to"
          {...commonTextFieldProps}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue(`expenses.${index}.isEdited`, true);
            setFieldValue(`expenses.${index}.paidTo`, e.target.value);
          }}
        />
      </Grid>

      <Grid item xs={12} sm={3}>
        <Field
          as={TextField}
          label="Challan Number"
          name={`expenses.${index}.challanNumber`}
          fullWidth
          placeholder="Enter challan number"
          {...commonTextFieldProps}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue(`expenses.${index}.isEdited`, true);
            setFieldValue(`expenses.${index}.challanNumber`, e.target.value);
          }}
        />
      </Grid>

      <Grid item xs={12} sm={3}>
        <Field
          as={TextField}
          select
          label="Action"
          name={`expenses.${index}.challanAction`}
          fullWidth
          required
          value={currentActionType}
          {...commonTextFieldProps}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue(`expenses.${index}.isEdited`, true);
            setFieldValue(`expenses.${index}.challanAction`, e.target.value);
          }}
          error={Boolean(
            expenseErrors?.challanAction &&
              touched.expenses?.[index]?.challanAction
          )}
          helperText={
            expenseErrors?.challanAction &&
            touched.expenses?.[index]?.challanAction
              ? expenseErrors?.challanAction
              : null
          }
        >
          {ACTION_TYPES.map((action) => (
            <MenuItem key={action} value={action}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {action}
              </Box>
            </MenuItem>
          ))}
        </Field>
      </Grid>

      {/* Amount */}
      <Grid item xs={12} sm={3}>
        <Field name={`expenses.${index}.amount`}>
          {({ field, meta }: any) => (
            <TextField
              {...field}
              {...commonTextFieldProps}
              label="Amount"
              fullWidth
              required
              error={meta.touched && Boolean(meta.error)}
              helperText={meta.touched && meta.error}
              value={field.value ? formatInputCurrency(field.value) : ""}
              onChange={(e) => {
                const value = e.target.value.replace(/,/g, "");
                if (value === "" || /^\d*\.?\d*$/.test(value)) {
                  field.onChange({
                    target: {
                      name: field.name,
                      value: value,
                    },
                  });
                  setFieldValue(`expenses.${index}.amount`, value);
                  setFieldValue(`expenses.${index}.isEdited`, true);
                }
              }}
              InputProps={{
                startAdornment: (
                  <Typography
                    component="span"
                    sx={{ mr: 0.5, fontSize: "0.75rem" }}
                  >
                    ₹
                  </Typography>
                ),
              }}
              inputProps={{
                inputMode: "decimal",
              }}
            />
          )}
        </Field>
      </Grid>

      <Grid item xs={12} sm={3}>
        <Field
          as={TextField}
          select
          label="Responsible Party"
          name={`expenses.${index}.responsibleParty`}
          fullWidth
          required
          value={currentResponsibleParty || ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue(`expenses.${index}.responsibleParty`, e.target.value);
            setFieldValue(`expenses.${index}.isEdited`, true);
          }}
          {...commonTextFieldProps}
        >
          {RESPONSIBLE_PARTY_CATEGORIES.map((category) => (
            <MenuItem key={category} value={category}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {category === "DRIVER" ? (
                  <AccountCircleIcon fontSize="small" />
                ) : (
                  <BusinessIcon fontSize="small" />
                )}
                {category}
              </Box>
            </MenuItem>
          ))}
        </Field>
      </Grid>

      {/* Staff Selection Autocomplete */}
      {currentResponsibleParty === "DRIVER" && (
        <Grid container item xs={12} sm={3}>
          <Grid item xs={selectedStaff ? 11 : 12}>
            <Autocomplete
              freeSolo
              options={staffOptions}
              loading={isLoading}
              getOptionLabel={getOptionLabel}
              value={selectedStaff}
              onChange={(_, newValue, reason) => {
                if (reason === "selectOption" && typeof newValue === "object") {
                  handleStaffChange(newValue);
                } else if (reason === "clear") {
                  handleStaffChange(null);
                }
              }}
              onInputChange={(_, inputValue, reason) => {
                setSearchTerm(inputValue);

                if (reason === "input") {
                  const matchingOption = staffOptions.find((option) =>
                    getOptionLabel(option)
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  );

                  if (!matchingOption && inputValue.trim() !== "") {
                    handleStaffChange(null, inputValue);
                  }
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`Select ${currentResponsibleParty}`}
                  placeholder={`Search from master data or enter manually...`}
                  required
                  fullWidth
                  error={
                    !!searchError ||
                    (!!expenseErrors?.responsiblePartyId &&
                      touched.expenses?.[index]?.responsiblePartyId)
                  }
                  helperText={
                    searchError
                      ? searchError
                      : expenseErrors?.responsiblePartyId
                      ? "Driver is required"
                      : null
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  {...commonTextFieldProps}
                />
              )}
              renderOption={renderStaffOption}
              noOptionsText={
                isLoading
                  ? "Searching..."
                  : searchError
                  ? "Error loading options"
                  : `No matching ${currentResponsibleParty.toLowerCase()} found. You can still type to add manually.`
              }
            />
          </Grid>
          {selectedStaff && (
            <Grid item xs={1}>
              <IconButton
                title={
                  displayMasterData ? "Hide Master Data" : "Display Master Data"
                }
                onClick={() => setDisplayMasterData(!displayMasterData)}
              >
                <Visibility
                  fontSize="small"
                  sx={{ fontSize: "1rem" }}
                  color="primary"
                />
              </IconButton>
            </Grid>
          )}
        </Grid>
      )}

      {/* Contact Number (manual entry) */}
      {(isManualEntry ||
        (!selectedStaff &&
          values.expenses[index]?.responsiblePartyContact)) && (
        <Grid item xs={12} sm={3}>
          <Field
            as={TextField}
            label="Contact Number"
            name={`expenses.${index}.responsiblePartyContact`}
            fullWidth
            placeholder="Enter contact number (optional)"
            {...commonTextFieldProps}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue(`expenses.${index}.isEdited`, true);
              setFieldValue(
                `expenses.${index}.responsiblePartyContact`,
                e.target.value
              );
            }}
            inputProps={{
              maxLength: 10,
            }}
            error={Boolean(
              expenseErrors?.responsiblePartyContact &&
                touched.expenses?.[index]?.responsiblePartyContact
            )}
            helperText={
              expenseErrors?.responsiblePartyContact &&
              touched.expenses?.[index]?.responsiblePartyContact
            }
          />
        </Grid>
      )}

      {/* Location */}
      <Grid item xs={12} sm={3}>
        <Field
          as={TextField}
          label="Location"
          name={`expenses.${index}.location`}
          fullWidth
          placeholder="Enter location"
          {...commonTextFieldProps}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue(`expenses.${index}.isEdited`, true);
            setFieldValue(`expenses.${index}.location`, e.target.value);
          }}
        />
      </Grid>

      {/* Auto-filled Staff Details */}
      {selectedStaff && !isManualEntry && displayMasterData && (
        <Grid item xs={11}>
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "primary.light",
              bgcolor: "#F0F7FF",
              mb: 1.5,
            }}
          >
            <Typography variant="subtitle2" color="primary.dark" gutterBottom>
              {currentResponsibleParty} Details (From Master Data)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.75rem" }}
                >
                  Name: {selectedStaff.name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.75rem" }}
                >
                  Contact: {selectedStaff.contactNumber}
                </Typography>
              </Grid>
              {currentResponsibleParty === "DRIVER" &&
                selectedStaff.drivingLicense && (
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "0.75rem" }}
                    >
                      License: {selectedStaff.drivingLicense}
                    </Typography>
                  </Grid>
                )}
            </Grid>
          </Box>
        </Grid>
      )}

      <Grid item xs={12}>
        <Button
          variant="outlined"
          onClick={() => setAddNotes(!addNotes)}
          size={smallButtonProp.size}
          sx={{
            ...smallButtonProp.sx,
            color: "grey",
            borderColor: "grey.400",
            "&:hover": {
              borderColor: "grey.400",
              color: "grey",
            },
          }}
        >
          <Typography variant="body2" sx={{ fontSize: "0.65rem" }}>
            {addNotes ? "Hide Notes" : "Add Notes"}
          </Typography>
        </Button>
        {Number(values.expenses[index].amount) > 0 &&
          values.expenses[index].payments?.length === 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    values.expenses[index].payments?.length! &&
                    values.expenses[index].payments?.length! > 0
                      ? true
                      : false
                  }
                  onChange={(e) => {
                    handleMarkAsPaid(e.target.checked);
                  }}
                  sx={{
                    "& .MuiSvgIcon-root": { fontSize: "0.7rem" },
                  }}
                />
              }
              label="Mark as Paid"
              sx={{
                p: 0,
                pr: 1,
                ml: 0.75,
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.7rem",
                  color: "primary.main",
                },
              }}
            />
          )}
      </Grid>

      {/* Notes */}
      {addNotes && (
        <Grid item xs={12}>
          <Field
            as={TextField}
            label="Notes"
            name={`expenses.${index}.notes`}
            fullWidth
            multiline
            rows={2}
            placeholder="Any additional notes about this expense..."
            {...commonTextFieldProps}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue(`expenses.${index}.isEdited`, true);
              setFieldValue(`expenses.${index}.notes`, e.target.value);
            }}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default ChallanExpense;
